import { useCallback, useState } from 'react';
import type { LiksDocumentPreviewItem } from '../../../components/liks/LiksDocumentPreviewModal/LiksDocumentPreviewModal';
import { getDocumentLabel } from '../../../features/knowledge/documentActionEligibility';
import type { useKnowledgeDocumentsTab } from '../../../features/knowledge/hooks/useKnowledgeDocumentsTab';
import { KnowledgeDocumentsDialogs } from './KnowledgeDocumentsDialogs';
import { KnowledgeDocumentsTable } from './KnowledgeDocumentsTable';
import { KnowledgeDocumentsToolbar } from './KnowledgeDocumentsToolbar';
import '../knowledgeDatabaseShared.css';
import './KnowledgeDocumentsToolbar.css';
import './KnowledgeDocumentsTable.css';

type KnowledgeDocumentsTabProps = {
  documentsTab: ReturnType<typeof useKnowledgeDocumentsTab>;
};

export function KnowledgeDocumentsTab({
  documentsTab,
}: KnowledgeDocumentsTabProps) {
  const {
    locale,
    translate,
    databaseId,
    publishState,
    canManageDocuments,
    fileInputRef,
    folderInputRef,
    documentsQuery,
    documents,
    embeddingProgressByDocumentId,
    documentSort,
    documentPage,
    documentSearchInput,
    sourceFilterValue,
    statusFilterValue,
    hasActiveDocumentFilters,
    handleDocumentSearchChange,
    handleDocumentSortToggle,
    handleSourceFilterChange,
    handleStatusFilterChange,
    handleUpload,
    handleFolderUpload,
    isUploading,
    uploadProgress,
    isUploadDragOver,
    handleUploadDragEnter,
    handleUploadDragLeave,
    handleUploadDragOver,
    handleUploadDrop,
    openFilePicker,
    openFolderPicker,
    handleDownload,
    handleDownloadDocuments,
    handleResumeAbandonedDocument,
    handleResumeAbandonedDocuments,
    requestRetryDocuments,
    requestDeleteDocuments,
    handleManualRefresh,
    pendingDelete,
    setPendingDelete,
    confirmDeleteDocument,
    selectedDocumentIds,
    selectedDocuments,
    hasSelection,
    allVisibleSelected,
    someVisibleSelected,
    toggleDocumentSelected,
    toggleSelectAllVisible,
    isBulkActionRunning,
    bulkConfirm,
    setBulkConfirm,
    confirmBulkDeleteDocuments,
    confirmBulkRetryDocuments,
    downloadBulkRetryDocuments,
    isDeletingDocument,
    bulkDeleteProgress,
    isRetryDownloading,
    isRetryConfirming,
    isRetryConfirmDisabled,
    bulkRetryDownloadProgress,
    bulkRetryProgress,
    allRetryFilesPrepared,
  } = documentsTab;
  const [previewItems, setPreviewItems] = useState<
    LiksDocumentPreviewItem[] | null
  >(null);
  const [previewInitialIndex, setPreviewInitialIndex] = useState(0);

  const handlePreview = useCallback((docs: typeof documents) => {
    const items: LiksDocumentPreviewItem[] = docs.map((document) => ({
      id: document.id,
      title: getDocumentLabel(document),
      isReadyForPreview: true,
    }));
    setPreviewInitialIndex(0);
    setPreviewItems(items);
  }, []);

  const emptyMessageKey =
    publishState === 'STAGING'
      ? 'knowledge.stagingDocumentsEmpty'
      : 'knowledge.documentsEmpty';
  const noResultsMessageKey =
    publishState === 'STAGING'
      ? 'knowledge.stagingDocumentsNoResults'
      : 'knowledge.documentsNoResults';

  return (
    <>
      <section
        className="knowledge-database__panel"
        onDragEnter={canManageDocuments ? handleUploadDragEnter : undefined}
        onDragLeave={canManageDocuments ? handleUploadDragLeave : undefined}
        onDragOver={canManageDocuments ? handleUploadDragOver : undefined}
        onDrop={canManageDocuments ? handleUploadDrop : undefined}
      >
        <KnowledgeDocumentsToolbar
          canManageDocuments={canManageDocuments}
          documentSearchInput={documentSearchInput}
          sourceFilterValue={sourceFilterValue}
          statusFilterValue={statusFilterValue}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          isUploadDragOver={isUploadDragOver}
          fileInputRef={fileInputRef}
          folderInputRef={folderInputRef}
          translate={translate}
          onDocumentSearchChange={handleDocumentSearchChange}
          onSourceFilterChange={handleSourceFilterChange}
          onStatusFilterChange={handleStatusFilterChange}
          onUpload={handleUpload}
          onFolderUpload={handleFolderUpload}
          openFilePicker={openFilePicker}
          openFolderPicker={openFolderPicker}
          onManualRefresh={handleManualRefresh}
        />
        {documentsQuery.isLoading ? (
          <p className="knowledge-database__empty">
            {translate('knowledge.loading')}
          </p>
        ) : documents.length === 0 ? (
          <p className="knowledge-database__empty">
            {hasActiveDocumentFilters || documentPage > 1
              ? translate(noResultsMessageKey)
              : translate(emptyMessageKey)}
          </p>
        ) : (
          <KnowledgeDocumentsTable
            documents={documents}
            locale={locale}
            canManageDocuments={canManageDocuments}
            documentSort={documentSort}
            selectedDocumentIds={selectedDocumentIds}
            selectedDocuments={selectedDocuments}
            hasSelection={hasSelection}
            allVisibleSelected={allVisibleSelected}
            someVisibleSelected={someVisibleSelected}
            isBulkActionRunning={isBulkActionRunning}
            embeddingProgressByDocumentId={embeddingProgressByDocumentId}
            publishState={publishState}
            translate={translate}
            onDocumentSortToggle={handleDocumentSortToggle}
            onToggleSelectAllVisible={toggleSelectAllVisible}
            onToggleDocumentSelected={toggleDocumentSelected}
            onPreview={handlePreview}
            onDownloadDocuments={handleDownloadDocuments}
            onResumeAbandonedDocuments={handleResumeAbandonedDocuments}
            onRetryDocuments={requestRetryDocuments}
            onDeleteDocuments={requestDeleteDocuments}
            onDownload={handleDownload}
            onResumeAbandonedDocument={handleResumeAbandonedDocument}
          />
        )}
      </section>

      <KnowledgeDocumentsDialogs
        databaseId={databaseId}
        pendingDelete={
          pendingDelete?.type === 'document'
            ? {
                type: 'document',
                id: pendingDelete.id,
                label: pendingDelete.label ?? pendingDelete.id,
              }
            : null
        }
        bulkConfirm={bulkConfirm}
        previewItems={previewItems}
        previewInitialIndex={previewInitialIndex}
        isDeletingDocument={isDeletingDocument}
        isBulkDeleting={isBulkActionRunning && bulkConfirm?.type === 'delete'}
        bulkDeleteProgress={bulkDeleteProgress}
        isRetryDownloading={isRetryDownloading}
        isRetryConfirming={isRetryConfirming}
        isRetryConfirmDisabled={isRetryConfirmDisabled}
        bulkRetryDownloadProgress={bulkRetryDownloadProgress}
        bulkRetryProgress={bulkRetryProgress}
        allRetryFilesPrepared={allRetryFilesPrepared}
        translate={translate}
        onConfirmDeleteDocument={() => void confirmDeleteDocument()}
        onCancelPendingDelete={() => {
          if (!isDeletingDocument) {
            setPendingDelete(null);
          }
        }}
        onConfirmBulkDelete={() => void confirmBulkDeleteDocuments()}
        onConfirmBulkRetry={() => void confirmBulkRetryDocuments()}
        onDownloadBulkRetry={() => void downloadBulkRetryDocuments()}
        onCancelBulkConfirm={() => {
          if (!(
            (isBulkActionRunning && bulkConfirm?.type === 'delete') ||
            isRetryDownloading ||
            isRetryConfirming
          )) {
            setBulkConfirm(null);
          }
        }}
        onClosePreview={() => {
          setPreviewItems(null);
          setPreviewInitialIndex(0);
        }}
      />
    </>
  );
}
